import type { ApiErrorBody, ApiErrorCodeLike, ApiErrorDetail } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code?: ApiErrorCodeLike;
  readonly shouldRequestNew?: boolean;

  constructor(opts: {
    status: number;
    code?: ApiErrorCodeLike;
    message: string;
    shouldRequestNew?: boolean;
  }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.shouldRequestNew = opts.shouldRequestNew;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

const statusFallback = (status: number): string | undefined => {
  if (status === 401) {
    return "Your session has expired. Please request a new access link.";
  }
  if (status === 403) {
    return "You do not have access to this resource.";
  }
  if (status >= 500) {
    return "Something went wrong on our end. Please try again later.";
  }
  return undefined;
};

const extractFromDetail = (
  detail: ApiErrorBody["detail"]
): { message?: string; code?: string | null } => {
  if (typeof detail === "string") {
    return { message: detail };
  }
  if (Array.isArray(detail)) {
    return {};
  }
  if (detail && typeof detail === "object") {
    const obj = detail as ApiErrorDetail;
    return { message: obj.message ?? obj.detail, code: obj.error_code };
  }
  return {};
};

export const parseApiError = async (
  response: Response,
  fallbackMessage: string
): Promise<ApiError> => {
  let body: ApiErrorBody | undefined;
  let parsedBody = false;
  try {
    body = (await response.json()) as ApiErrorBody;
    parsedBody = true;
  } catch {
    body = undefined;
  }

  const fromDetail = extractFromDetail(body?.detail);
  const serverMessage = body?.message ?? fromDetail.message;
  const message =
    serverMessage ??
    (parsedBody ? statusFallback(response.status) : undefined) ??
    fallbackMessage;
  const code = body?.error_code ?? fromDetail.code ?? undefined;

  return new ApiError({
    status: response.status,
    code,
    message,
    shouldRequestNew: body?.should_request_new,
  });
};
